import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCustomization } from "@/context/CustomizationContext";
import { fmtCurrency, fmtDate, fmtNum } from "@/lib/format";
import { getEffectiveWorkspaceColumns } from "@/lib/workspaceColumns";
import {
  splitMulti, stateBadgeStyle, specialtyBadgeStyle, statusRowStyle, statusBadgeStyle,
} from "@/lib/colorCoding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ExportButton from "@/components/common/ExportButton";
import PresetsMenu from "@/components/common/PresetsMenu";
import MultiSelectFilter from "@/components/common/MultiSelectFilter";
import ImportDialog from "@/components/common/ImportDialog";
import { toast } from "sonner";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

/**
 * Derive engagement status from start/end dates relative to today.
 *  Pending  — today is before start_date
 *  Active   — today is on or between start_date and end_date (inclusive)
 *  Inactive — today is after end_date
 * Missing dates are treated as "no constraint" for that bound.
 */
function deriveStatus(startDate, endDate) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const s = startDate ? new Date(startDate + "T00:00:00") : null;
  const e = endDate ? new Date(endDate + "T00:00:00") : null;
  if (s && today < s) return "Pending";
  if (e && today > e) return "Inactive";
  return "Active";
}

const RIGHT_TYPES = new Set(["number", "currency", "percentage"]);
const PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const STATUS_SUMMARY_CARDS = [
  { label: "Active", key: "active" },
  { label: "Inactive", key: "inactive" },
  { label: "Pending", key: "pending" },
];

const EMPTY_FORM = {
  engmt_id: "", order_crf_num: "", order_crf_date: "", client_name: "",
  service_key: "", process: "", service: "", rev_code: "", fte: "", pricing: "",
  engmt_type: "New", start_date: "", end_date: "", duration_months: "", notes: "",
  state: "", specialty: "", billing_system: "", poc: "", custom: {},
};

export default function BusinessTrackerPage() {
  const { user } = useAuth();
  const canEdit = Boolean(user?.role);
  const canDelete = ["super_admin", "cxo"].includes(user?.role);
  const canViewFinancial = ["super_admin", "cxo", "strategy"].includes(user?.role);
  const { getColumns, colorMaps, sectionLabels } = useCustomization();
  const sectionTitle = sectionLabels?.business_tracker || "Business Tracker";
  const detailsTitle = sectionLabels?.revenue_details || "Revenue Details";
  const [data, setData] = useState({ items: [], total: 0, stats: {} });
  const [meta, setMeta] = useState({ engagement_clients: [], processes: [], statuses: [] });
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [clientFilter, setClientFilter] = useState([]);
  const [cellEdit, setCellEdit] = useState(null); // {item, col, value}
  const [sortBy, setSortBy] = useState("client_name");
  const [sortDir, setSortDir] = useState("asc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ---- Control Panel driven layout ----
  const configuredColumns = getColumns("business_tracker");
  const allColumns = useMemo(
    () => getEffectiveWorkspaceColumns("business_tracker", configuredColumns),
    [configuredColumns],
  );
  const columns = useMemo(() => allColumns.filter((c) => c.visible !== false), [allColumns]);
  const customCols = useMemo(() => allColumns.filter((c) => !c.core && c.visible !== false), [allColumns]);
  const colByKey = useMemo(() => Object.fromEntries(allColumns.map((c) => [c.key, c])), [allColumns]);
  const labelOf = (key) => colByKey[key]?.label || key;
  const statusOptions = colByKey.status?.options?.length ? colByKey.status.options : ["Active", "Inactive", "Pending"];

  // ---- Multi-select helpers ----
  const allPageIds = useMemo(() => data.items.map((i) => i.id), [data.items]);
  const allSelected = allPageIds.length > 0 && allPageIds.every((id) => selected.has(id));
  const someSelected = !allSelected && allPageIds.some((id) => selected.has(id));
  const toggleRow = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allPageIds));
  const toggleStatusCardFilter = (status) => {
    setStatusFilter((current) => current.includes(status)
      ? current.filter((value) => value !== status)
      : [...current, status]);
    setPage(1);
    setSelected(new Set());
  };

  const loadMeta = useCallback(() => {
    api.get("/meta").then((res) => setMeta(res.data)).catch(() => {});
    api.get("/services").then((res) => setServices(res.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir };
    if (search) params.search = search;
    if (statusFilter.length) params.status = statusFilter.join(",");
    if (clientFilter.length) params.client_name = clientFilter.join(",");
    api.get("/engagements", { params })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load engagements"))
      .finally(() => setLoading(false));
  }, [page, pageSize, search, statusFilter, clientFilter, sortBy, sortDir]);

  useEffect(loadMeta, [loadMeta]);
  useEffect(load, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); setSelected(new Set()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const currentFilters = {
    search: searchInput, statuses: statusFilter, clients: clientFilter, sort_by: sortBy, sort_dir: sortDir,
  };

  const asArray = (v, legacy) => Array.isArray(v) ? v : (legacy && legacy !== "all" ? [legacy] : []);

  const applyPreset = useCallback((f) => {
    setSearchInput(f.search || "");
    setSearch(f.search || "");
    setStatusFilter(asArray(f.statuses, f.status));
    setClientFilter(asArray(f.clients, f.client));
    setSortBy(f.sort_by || "client_name");
    setSortDir(f.sort_dir || "asc");
    setPage(1);
    setSelected(new Set());
  }, []);

  const fmtCustom = (col, v) => {
    if (col.type === "currency") {
      if (!canViewFinancial) return <span className="text-slate-400">$</span>;
      if (v == null) return "-";
      return fmtCurrency(v, col.decimals ?? 2);
    }
    if (col.type === "percentage") {
      if (!canViewFinancial) return <span className="text-slate-400">%</span>;
      if (v == null) return "-";
      return `${fmtNum(v, col.decimals ?? 2)}%`;
    }
    if (v == null || v === "") return "-";
    if (col.type === "number") return fmtNum(v, col.decimals ?? 2);
    if (col.type === "date") return fmtDate(v);
    if (col.type === "boolean") return v ? "Yes" : "No";
    return String(v);
  };

  const openCellEdit = (item, col) => {
    if (!canEdit) return;
    if (col.key === "status") return; // status is auto-derived from dates — not manually editable
    if (col.key === "rev_code" || col.key === "pricing") return; // service-derived fields
    if (col.financial && !canViewFinancial) return; // financial fields restricted
    const raw = col.core ? item[col.key] : item.custom?.[col.key];
    let value = raw ?? "";
    if (col.type === "boolean") value = raw == null ? "" : String(!!raw);
    setCellEdit({ item, col, value });
  };

  const saveCell = async () => {
    const { item, col, value } = cellEdit;
    const field = col.key;
    if ((field === "engmt_id" || field === "client_name") && !String(value).trim()) {
      toast.error(`${col.label} is required.`);
      return;
    }
    const isNum = RIGHT_TYPES.has(col.type);
    let newVal = isNum ? (parseFloat(value) || 0) : value;
    if (!col.core) {
      if (isNum) newVal = value === "" ? null : (parseFloat(value) || 0);
      else if (col.type === "boolean") newVal = value === "" ? null : value === "true";
    }
    let duration = item.duration_months ?? 0;
    if (field === "duration_months") duration = newVal;
    else if (field === "start_date" || field === "end_date") duration = null; // recalc from dates
    // Status is always derived from dates by the backend — pass the computed value
    const updatedStart = field === "start_date" ? newVal : (item.start_date || "");
    const updatedEnd = field === "end_date" ? newVal : (item.end_date || "");
    const payload = {
      status: deriveStatus(updatedStart, updatedEnd),
      engmt_id: field === "engmt_id" ? String(value).trim() : item.engmt_id,
      order_crf_num: field === "order_crf_num" ? newVal : (item.order_crf_num || ""),
      order_crf_date: field === "order_crf_date" ? newVal : (item.order_crf_date || ""),
      client_name: field === "client_name" ? String(value).trim() : item.client_name,
      process: field === "process" ? String(newVal).trim() : (item.process || ""),
      service: field === "service" ? newVal : (item.service || ""),
      rev_code: item.rev_code || "", // always refreshed from the matching Services record
      fte: field === "fte" ? newVal : (item.fte || 0),
      pricing: field === "pricing" ? newVal : (item.pricing || 0),
      engmt_type: field === "engmt_type" ? newVal : (item.engmt_type || "New"),
      start_date: field === "start_date" ? newVal : (item.start_date || ""),
      end_date: field === "end_date" ? newVal : (item.end_date || ""),
      duration_months: duration,
      notes: field === "notes" ? newVal : (item.notes || ""),
      state: field === "state" ? newVal : (item.state || ""),
      specialty: field === "specialty" ? newVal : (item.specialty || ""),
      billing_system: field === "billing_system" ? newVal : (item.billing_system || ""),
      poc: field === "poc" ? newVal : (item.poc || ""),
      custom: col.core ? (item.custom || {}) : { ...(item.custom || {}), [field]: newVal },
    };
    setSaving(true);
    try {
      const res = await api.put(`/engagements/${item.id}`, payload);
      const c = res.data?.cascade;
      if (c && (c.revenue_details || c.engagements)) {
        toast.success(`Client renamed everywhere — ${c.revenue_details} revenue line(s) and ${c.engagements} engagement(s) updated`);
      } else {
        toast.success(`${col.label} updated`);
      }
      setCellEdit(null);
      load();
      loadMeta();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleSort = (field) => {
    if (!field) return;
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(1);
    setSelected(new Set());
  };

  const openAdd = () => { setEditing(null); setForm({ ...EMPTY_FORM, custom: {} }); setDialogOpen(true); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      engmt_id: item.engmt_id || "",
      order_crf_num: item.order_crf_num || "",
      order_crf_date: item.order_crf_date || "",
      client_name: item.client_name || "",
      service_key: "",
      process: item.process || "",
      service: item.service || "",
      rev_code: item.rev_code || "",
      fte: item.fte ?? "",
      pricing: item.pricing ?? "",
      engmt_type: item.engmt_type || "New",
      start_date: item.start_date || "",
      end_date: item.end_date || "",
      duration_months: item.duration_months ?? "",
      notes: item.notes || "",
      state: item.state || "",
      specialty: item.specialty || "",
      billing_system: item.billing_system || "",
      poc: item.poc || "",
      custom: { ...(item.custom || {}) },
    });
    setDialogOpen(true);
  };

  const onServiceSelect = (svcId) => {
    const svc = services.find((s) => s.id === svcId);
    if (svc) {
      setForm((f) => ({
        ...f, service_key: svcId, service: svc.service, process: svc.process,
        rev_code: svc.rev_code, pricing: svc.pricing,
      }));
    }
  };

  const setCustomField = (key, value) => setForm((f) => ({ ...f, custom: { ...f.custom, [key]: value } }));

  const save = async () => {
    if (!form.engmt_id || !form.client_name || !form.process || !form.service) {
      toast.error("Engagement ID, client name, Process, and Service are required.");
      return;
    }
    setSaving(true);
    const custom = {};
    customCols.forEach((c) => {
      let v = form.custom?.[c.key];
      if (v === "" || v == null) { custom[c.key] = null; return; }
      if (RIGHT_TYPES.has(c.type)) v = parseFloat(v) || 0;
      else if (c.type === "boolean") v = v === true || v === "true";
      custom[c.key] = v;
    });
    const payload = {
      ...form,
      // Status is always derived from dates — never manually supplied
      status: deriveStatus(form.start_date, form.end_date),
      fte: parseFloat(form.fte) || 0,
      pricing: parseFloat(form.pricing) || 0,
      duration_months: form.duration_months !== "" ? parseFloat(form.duration_months) : null,
      custom,
    };
    delete payload.service_key;
    try {
      if (editing) {
        const res = await api.put(`/engagements/${editing.id}`, payload);
        const c = res.data?.cascade;
        if (c && (c.revenue_details || c.engagements)) {
          toast.success(`Engagement updated — client renamed across ${c.revenue_details} revenue line(s) and ${c.engagements} engagement(s)`);
        } else {
          toast.success("Engagement updated");
        }
      } else {
        await api.post("/engagements", payload);
        toast.success("Engagement added");
      }
      setDialogOpen(false);
      load();
      loadMeta();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/engagements/${deleting.id}`);
      toast.success("Engagement deleted");
      setDeleting(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const confirmBulkDelete = async () => {
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => api.delete(`/engagements/${id}`)));
      toast.success(`${ids.length} engagement${ids.length !== 1 ? "s" : ""} deleted`);
      setSelected(new Set());
      setBulkDeleting(false);
      load();
    } catch {
      toast.error("Some deletions failed — please retry");
    }
  };

  // ---- Cell renderers (Control Panel drives order/visibility/labels) ----
  const renderCell = (item, col) => {
    const key = col.key;
    const editableTd = canEdit && !(col.financial && !canViewFinancial) ? "cursor-pointer hover:bg-white/60" : "";
    const tdTitle = editableTd ? "Click to edit this value" : undefined;
    if (!col.core) {
      return (
        <td key={key} className={`px-3 py-2 max-w-[160px] truncate ${RIGHT_TYPES.has(col.type) ? "text-right tabular-nums" : ""} ${editableTd}`}
          title={tdTitle} onClick={() => openCellEdit(item, col)} data-testid={`bt-cell-${key}-${item.id}`}>
          {fmtCustom(col, item.custom?.[key])}
        </td>
      );
    }
    switch (key) {
      case "status": {
        const derived = deriveStatus(item.start_date, item.end_date);
        return (
          <td key={key} className="px-3 py-2" title="Status is automatically derived from Start and End dates"
            data-testid={`bt-cell-status-${item.id}`}>
            <Badge style={statusBadgeStyle(derived, colorMaps.status)}
              className="rounded-full font-medium text-[10px] px-2 border">
              {derived}
            </Badge>
          </td>
        );
      }
      case "engmt_id":
        return (
          <td key={key} className={`px-3 py-2 font-mono whitespace-nowrap ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)}>{item.engmt_id}</td>
        );
      case "client_name":
        return (
          <td key={key} className={`px-3 py-2 max-w-[200px] truncate font-semibold ${editableTd}`} title={item.client_name}
            onClick={() => openCellEdit(item, col)} data-testid={`bt-cell-client-${item.id}`}>{item.client_name}</td>
        );
      case "service":
        return (
          <td key={key} className={`px-3 py-2 max-w-[160px] truncate ${editableTd}`} title={item.service}
            onClick={() => openCellEdit(item, col)}>{item.service}</td>
        );
      case "fte":
        return (
          <td key={key} className={`px-3 py-2 text-right tabular-nums ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)} data-testid={`bt-cell-fte-${item.id}`}>{fmtNum(item.fte, col.decimals ?? 2)}</td>
        );
      case "pricing":
        return (
          <td key={key} className={`px-3 py-2 text-right tabular-nums ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)} data-testid={`bt-cell-pricing-${item.id}`}>
            {!canViewFinancial ? <span className="opacity-60">$</span> : item.pricing == null ? <span className="opacity-60">$</span> : fmtCurrency(item.pricing, col.decimals ?? 0)}
          </td>
        );
      case "order_crf_date":
      case "start_date":
      case "end_date":
        return (
          <td key={key} className={`px-3 py-2 whitespace-nowrap tabular-nums ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)}>{fmtDate(item[key])}</td>
        );
      case "duration_months":
        return (
          <td key={key} className={`px-3 py-2 text-right tabular-nums ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)}>{fmtNum(item.duration_months, col.decimals ?? 0)}</td>
        );
      case "state":
        return (
          <td key={key} className={`px-3 py-2 ${editableTd}`} title={item.state || tdTitle}
            onClick={() => openCellEdit(item, col)} data-testid={`bt-cell-state-${item.id}`}>
            {splitMulti(item.state).length ? (
              <span className="inline-flex flex-wrap gap-1">
                {splitMulti(item.state).map((code) => (
                  <Badge key={code} style={stateBadgeStyle(code, colorMaps.state)}
                    className="rounded-full font-semibold text-[10px] px-2 border font-mono tracking-wide">
                    {code.toUpperCase()}
                  </Badge>
                ))}
              </span>
            ) : "-"}
          </td>
        );
      case "specialty":
        return (
          <td key={key} className={`px-3 py-2 max-w-[220px] ${editableTd}`} title={item.specialty || tdTitle}
            onClick={() => openCellEdit(item, col)} data-testid={`bt-cell-specialty-${item.id}`}>
            {splitMulti(item.specialty).length ? (
              <span className="inline-flex flex-wrap gap-1">
                {splitMulti(item.specialty).slice(0, 4).map((sp) => (
                  <Badge key={sp} style={specialtyBadgeStyle(sp, colorMaps.specialty)}
                    className="rounded-full font-medium text-[10px] px-2 border whitespace-nowrap max-w-[140px] truncate inline-block">
                    {sp}
                  </Badge>
                ))}
                {splitMulti(item.specialty).length > 4 && (
                  <Badge className="rounded-full text-[10px] px-2 border bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100">
                    +{splitMulti(item.specialty).length - 4}
                  </Badge>
                )}
              </span>
            ) : "-"}
          </td>
        );
      case "billing_system":
        return (
          <td key={key} className={`px-3 py-2 max-w-[140px] truncate ${editableTd}`} title={item.billing_system || tdTitle}
            onClick={() => openCellEdit(item, col)}>{item.billing_system || "-"}</td>
        );
      case "poc":
        return (
          <td key={key} className={`px-3 py-2 max-w-[150px] truncate opacity-80 ${editableTd}`} title={item.poc || tdTitle}
            onClick={() => openCellEdit(item, col)}>{item.poc || "-"}</td>
        );
      case "notes":
        return (
          <td key={key} className={`px-3 py-2 max-w-[160px] truncate opacity-70 ${editableTd}`} title={item.notes || tdTitle}
            onClick={() => openCellEdit(item, col)}>{item.notes && item.notes !== "-" ? item.notes : "-"}</td>
        );
      default:
        return (
          <td key={key} className={`px-3 py-2 ${editableTd}`} title={tdTitle}
            onClick={() => openCellEdit(item, col)}>{item[key] ?? "-"}</td>
        );
    }
  };

  const cellInput = () => {
    const { col, value } = cellEdit;
    if (col.type === "dropdown") {
      const opts = col.key === "status" ? statusOptions : (col.options || []);
      return (
        <Select value={String(value || "")} onValueChange={(v) => setCellEdit({ ...cellEdit, value: v })}>
          <SelectTrigger data-testid="bt-cell-edit-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === "boolean") {
      return (
        <Select value={String(value)} onValueChange={(v) => setCellEdit({ ...cellEdit, value: v })}>
          <SelectTrigger data-testid="bt-cell-edit-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        autoFocus
        data-testid="bt-cell-edit-input"
        type={RIGHT_TYPES.has(col.type) ? "number" : col.type === "date" ? "date" : "text"}
        step={RIGHT_TYPES.has(col.type) ? "0.01" : undefined}
        value={cellEdit.value}
        onChange={(e) => setCellEdit({ ...cellEdit, value: e.target.value })}
        onKeyDown={(e) => e.key === "Enter" && saveCell()}
      />
    );
  };

  return (
    <div className="space-y-4" data-testid="business-tracker-page">
      {/* Stats strip */}
      <div className="flex flex-wrap gap-3" data-testid="business-tracker-stats">
        <div className="rounded-lg border bg-white px-4 py-2 text-sm shadow-sm">
          <span className="text-slate-500">Engagements:</span>{" "}
          <span className="font-semibold tabular-nums">{data.stats.count ?? 0}</span>
        </div>
        <div className="rounded-lg border bg-white px-4 py-2 text-sm shadow-sm">
          <span className="text-slate-500">Contracted FTEs:</span>{" "}
          <span className="font-semibold tabular-nums">{fmtNum(data.stats.total_fte)}</span>
        </div>
        {STATUS_SUMMARY_CARDS.map(({ label, key }) => {
          const statusStyle = statusBadgeStyle(label, colorMaps.status);
          const selectedFilter = statusFilter.includes(label);
          return (
            <button
              key={key}
              type="button"
              data-testid={`business-tracker-${key}-summary-card`}
              aria-pressed={selectedFilter}
              title={`Toggle the ${label} status filter for the grid`}
              className={`rounded-lg border px-4 py-2 text-sm shadow-sm transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 ${
                selectedFilter ? "ring-2 ring-slate-400 ring-offset-1" : "hover:shadow"
              }`}
              style={{
                backgroundColor: statusStyle.backgroundColor,
                borderColor: statusStyle.borderColor,
                color: statusStyle.color,
              }}
              onClick={() => toggleStatusCardFilter(label)}
            >
              <span className="font-medium">{label}:</span>{" "}
              <span className="font-semibold tabular-nums">
                {data.stats[`${key}_count`] ?? 0} engagements · {fmtNum(data.stats[`${key}_fte`])} FTEs
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="business-tracker-search-input" placeholder="Search client, ID, POC, notes..."
            className="pl-9 rounded-lg" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <MultiSelectFilter label="Status" testId="business-tracker-status-filter" className="w-[140px]"
          options={meta.statuses}
          selected={statusFilter} onChange={(v) => { setStatusFilter(v); setPage(1); setSelected(new Set()); }} />
        <MultiSelectFilter label="Client" testId="business-tracker-client-filter" className="w-[200px]"
          options={meta.engagement_clients}
          selected={clientFilter} onChange={(v) => { setClientFilter(v); setPage(1); setSelected(new Set()); }} />
        {canDelete && selected.size > 0 && (
          <Button variant="destructive" size="sm" className="rounded-lg"
            data-testid="business-tracker-bulk-delete-button"
            onClick={() => setBulkDeleting(true)}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete {selected.size} selected
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <PresetsMenu pageKey="business-tracker" currentFilters={currentFilters} onApply={applyPreset} />
          {canViewFinancial && (
            <ImportDialog entity="engagements" entityLabel={sectionTitle}
              testIdPrefix="business-tracker" onImported={() => { load(); loadMeta(); }} />
          )}
          {canViewFinancial && <ExportButton testId="business-tracker-export-button" />}
          {canEdit && (
            <Button data-testid="business-tracker-add-engagement-button" onClick={openAdd} size="sm" className="rounded-lg">
              <Plus className="h-4 w-4 mr-1" /> Add Engagement
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border bg-white shadow-[var(--app-shadow-sm)] overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : data.items.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500" data-testid="business-tracker-empty">
            No engagements match your filters.
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-21rem)]">
            <table className="sticky-table w-full text-xs" data-testid="business-tracker-table">
              <thead>
                <tr className="border-b">
                  {canDelete && (
                    <th className="px-2 py-1 border-b w-8">
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={toggleAll}
                        aria-label="Select all"
                        data-testid="business-tracker-select-all"
                      />
                    </th>
                  )}
                  {columns.map((col) => {
                    const sortField = col.core ? col.key : `custom.${col.key}`;
                    const right = RIGHT_TYPES.has(col.type);
                    return (
                      <th key={col.key} className={`px-1 py-1 font-semibold text-slate-600 whitespace-nowrap border-b ${right ? "text-right" : "text-left"}`}>
                        <button
                          data-testid={`sort-${sortField}`}
                          onClick={() => toggleSort(sortField)}
                          title={`Sort by ${col.label}`}
                          className={`inline-flex items-center gap-1 rounded px-2 py-1.5 hover:bg-slate-100 transition-[background-color] duration-150 ${right ? "flex-row-reverse" : ""} ${sortBy === sortField ? "text-[hsl(var(--primary))]" : ""}`}
                        >
                          {col.label}
                          {sortBy === sortField
                            ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
                            : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </th>
                    );
                  })}
                  <th className="px-1 py-1 border-b"><span className="px-2" /></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id}
                    className="border-b last:border-0 transition-[background-color] duration-150 hover:brightness-[0.98]"
                    style={statusRowStyle(deriveStatus(item.start_date, item.end_date), colorMaps.status) || undefined}
                    data-testid={`engagement-row-${item.id}`}>
                    {canDelete && (
                      <td className="px-2 py-1.5">
                        <Checkbox
                          checked={selected.has(item.id)}
                          onCheckedChange={() => toggleRow(item.id)}
                          aria-label="Select row"
                          data-testid={`engagement-select-${item.id}`}
                        />
                      </td>
                    )}
                    {columns.map((col) => renderCell(item, col))}
                    <td className="px-2 py-2">
                      {canEdit && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`engagement-actions-${item.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(item)} className="cursor-pointer" data-testid={`engagement-edit-${item.id}`}>
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                          </DropdownMenuItem>
                          {canDelete && (
                          <DropdownMenuItem onClick={() => setDeleting(item)} className="cursor-pointer text-red-600 focus:text-red-600" data-testid={`engagement-delete-${item.id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && data.total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2.5 text-xs text-slate-500">
            <span>Page {page} of {totalPages} · {data.total} engagements</span>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <div className="flex items-center gap-1.5">
                <label htmlFor="business-tracker-page-size" className="whitespace-nowrap">Rows per page</label>
                <Select value={String(pageSize)} onValueChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                  setSelected(new Set());
                }}>
                  <SelectTrigger id="business-tracker-page-size" className="h-7 w-[76px] rounded-lg px-2" data-testid="business-tracker-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" className="h-7 w-7 rounded-lg p-0" disabled={page <= 1}
                onClick={() => setPage(1)} data-testid="business-tracker-first-page" title="First page">
                <ChevronsLeft className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 rounded-lg" disabled={page <= 1}
                onClick={() => setPage(page - 1)} data-testid="business-tracker-prev-page">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </Button>
              <Button variant="outline" size="sm" className="h-7 rounded-lg" disabled={page >= totalPages}
                onClick={() => setPage(page + 1)} data-testid="business-tracker-next-page">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="h-7 w-7 rounded-lg p-0" disabled={page >= totalPages}
                onClick={() => setPage(totalPages)} data-testid="business-tracker-last-page" title="Last page">
                <ChevronsRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Engagement" : "Add Engagement"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update engagement details." : "Create a new client engagement. Duration auto-calculates from dates if left blank."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>{labelOf("engmt_id")} *</Label>
              <Input data-testid="engagement-form-id" placeholder="e.g. ASHC-ARF-004"
                value={form.engmt_id} onChange={(e) => setForm({ ...form, engmt_id: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("engmt_type")}</Label>
              <Select value={form.engmt_type} onValueChange={(v) => setForm({ ...form, engmt_type: v })}>
                <SelectTrigger data-testid="engagement-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Extension">Extension</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>{labelOf("client_name")} *</Label>
              <Input data-testid="engagement-form-client" list="eng-client-options" placeholder="Client name"
                value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
              <datalist id="eng-client-options">
                {meta.engagement_clients.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("service")} *</Label>
              <Select value={form.service_key} onValueChange={onServiceSelect}>
                <SelectTrigger data-testid="engagement-form-service">
                  <SelectValue placeholder={form.service || "Select service"} />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.process} · {s.service} · {s.service_code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("process")} *</Label>
              <Input data-testid="engagement-form-process" value={form.process} readOnly
                placeholder="Select a service" className="bg-slate-50 text-slate-600" />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("rev_code")}</Label>
              <Input data-testid="engagement-form-rev-code" value={form.rev_code} readOnly
                placeholder="Assigned from Services" className="bg-slate-50 font-mono text-slate-600" />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("fte")}</Label>
              <Input type="number" step="0.01" data-testid="engagement-form-fte" className="text-right tabular-nums"
                value={form.fte} onChange={(e) => setForm({ ...form, fte: e.target.value })} />
            </div>
            {canViewFinancial ? (
              <div className="space-y-1.5">
                <Label>{labelOf("pricing")} (per FTE)</Label>
                <Input type="number" step="0.01" data-testid="engagement-form-pricing" className="text-right tabular-nums"
                  value={form.pricing} readOnly />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>{labelOf("pricing")} (per FTE)</Label>
                <div className="h-9 flex items-center rounded-md border bg-slate-50 px-3 text-sm text-slate-400" data-testid="engagement-form-pricing-masked">
                  $ (assigned from catalog)
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{labelOf("order_crf_num")}</Label>
              <Input data-testid="engagement-form-crf" value={form.order_crf_num}
                onChange={(e) => setForm({ ...form, order_crf_num: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("order_crf_date")}</Label>
              <Input type="date" data-testid="engagement-form-crf-date" value={form.order_crf_date}
                onChange={(e) => setForm({ ...form, order_crf_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("state")}</Label>
              <Input data-testid="engagement-form-state" placeholder="e.g. TX" value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("start_date")}</Label>
              <Input type="date" data-testid="engagement-form-start" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("end_date")}</Label>
              <Input type="date" data-testid="engagement-form-end" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("duration_months")}</Label>
              <Input type="number" step="1" data-testid="engagement-form-duration" className="text-right tabular-nums"
                placeholder="Auto" value={form.duration_months}
                onChange={(e) => setForm({ ...form, duration_months: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("specialty")}</Label>
              <Input data-testid="engagement-form-specialty" value={form.specialty}
                onChange={(e) => setForm({ ...form, specialty: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("billing_system")}</Label>
              <Input data-testid="engagement-form-billing" placeholder="e.g. athenaOne" value={form.billing_system}
                onChange={(e) => setForm({ ...form, billing_system: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("poc")}</Label>
              <Input data-testid="engagement-form-poc" placeholder="Points of contact" value={form.poc}
                onChange={(e) => setForm({ ...form, poc: e.target.value })} />
            </div>
            <div className="col-span-2 md:col-span-3 space-y-1.5">
              <Label>{labelOf("notes")}</Label>
              <Textarea rows={2} data-testid="engagement-form-notes" placeholder="Optional notes (extensions, terminations, etc.)"
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            {customCols.length > 0 && (
              <>
                <div className="col-span-2 md:col-span-3 border-t pt-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Custom Fields</p>
                </div>
                {customCols.map((c) => (
                  <div key={c.key} className="space-y-1.5">
                    <Label>{c.label}</Label>
                    {c.financial && !canViewFinancial ? (
                      <div className="h-9 flex items-center rounded-md border bg-slate-50 px-3 text-sm text-slate-400">$</div>
                    ) : c.type === "dropdown" ? (
                      <Select value={String(form.custom?.[c.key] ?? "")}
                        onValueChange={(v) => setCustomField(c.key, v)}>
                        <SelectTrigger data-testid={`engagement-form-${c.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          {(c.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : c.type === "boolean" ? (
                      <Select value={form.custom?.[c.key] == null ? "" : String(form.custom[c.key])}
                        onValueChange={(v) => setCustomField(c.key, v === "true")}>
                        <SelectTrigger data-testid={`engagement-form-${c.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Yes</SelectItem>
                          <SelectItem value="false">No</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        data-testid={`engagement-form-${c.key}`}
                        type={RIGHT_TYPES.has(c.type) ? "number" : c.type === "date" ? "date" : "text"}
                        step={RIGHT_TYPES.has(c.type) ? "0.01" : undefined}
                        className={RIGHT_TYPES.has(c.type) ? "text-right tabular-nums" : ""}
                        value={form.custom?.[c.key] ?? ""}
                        onChange={(e) => setCustomField(c.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="engagement-form-save-button" onClick={save} disabled={saving}>
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Engagement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={bulkDeleting} onOpenChange={(o) => !o && setBulkDeleting(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} engagement{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selected.size} selected engagement{selected.size !== 1 ? "s" : ""}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="business-tracker-bulk-delete-confirm-button"
              onClick={confirmBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete {selected.size} engagement{selected.size !== 1 ? "s" : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete engagement?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && `${deleting.engmt_id} · ${deleting.client_name} · ${deleting.service}`}
              <br />This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="engagement-delete-confirm-button" onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Single-cell edit dialog */}
      <Dialog open={!!cellEdit} onOpenChange={(o) => !o && setCellEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit {cellEdit?.col.label || ""}</DialogTitle>
            <DialogDescription>
              {cellEdit && `${cellEdit.item.engmt_id} · ${cellEdit.item.client_name}`}
            </DialogDescription>
          </DialogHeader>
          {cellEdit && (
            <div className="space-y-1.5">
              <Label>{cellEdit.col.label}</Label>
              {cellInput()}
              {cellEdit.col.key === "client_name" && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  Renaming this client updates it everywhere in the application, including {detailsTitle}.
                </p>
              )}
              {(cellEdit.col.key === "start_date" || cellEdit.col.key === "end_date") && (
                <p className="text-[11px] text-muted-foreground">Duration (months) recalculates automatically.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCellEdit(null)}>Cancel</Button>
            <Button data-testid="bt-cell-edit-save" onClick={saveCell} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
